/**
 * Utility functions module
 * Common helper functions used throughout the application
 */

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Throttle function to limit function calls
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

/**
 * Format currency for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: EUR)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, currency = 'EUR') {
    // Match Django's server-side rendering (|floatformat:2 followed by " €"):
    // no digit grouping, decimal separator per page language. Always using
    // lv-LV showed "1 234,50 €" next to server-rendered "1234.50 €" in the
    // English UI. parseCurrency reads both forms back.
    const lang = (document.documentElement.lang || 'en').slice(0, 2);
    const fixed = Number(amount || 0).toFixed(2);
    const symbol = currency === 'EUR' ? '€' : currency;
    return `${lang === 'lv' ? fixed.replace('.', ',') : fixed} ${symbol}`;
}

/**
 * Return an ISO date (YYYY-MM-DD) shifted by a number of days.
 *
 * Works in UTC: parsing "YYYY-MM-DD" yields UTC midnight, and adding days in
 * local time then converting back with toISOString() came out one day early
 * across the spring DST change in Europe/Riga.
 */
export function addDaysToIsoDate(isoDate, days) {
    const date = new Date(`${isoDate}T00:00:00Z`);
    if (isNaN(date.getTime()) || isNaN(days)) return '';
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}

/**
 * Keep a due-date field in sync with an issue date and payment terms (days).
 */
export function bindDueDatePreview(issueDateInput, paymentTermsInput, dueDateInput) {
    if (!issueDateInput || !paymentTermsInput || !dueDateInput) return;
    const update = () => {
        if (!issueDateInput.value || !paymentTermsInput.value) return;
        const due = addDaysToIsoDate(issueDateInput.value, parseInt(paymentTermsInput.value, 10));
        if (due) dueDateInput.value = due;
    };
    issueDateInput.addEventListener('change', update);
    paymentTermsInput.addEventListener('change', update);
    update();
}

/**
 * Parse a displayed currency/number string back to a Number.
 *
 * Handles the lv-LV formatting produced by formatCurrency, where the decimal
 * separator is a comma and thousands are grouped with (non-breaking) spaces,
 * e.g. "1 234,50 €". A naive strip of commas turns "1 234,50" into 123450, so
 * commas must be treated as the decimal separator rather than removed.
 * @param {string|number} text - Value to parse
 * @returns {number} Parsed number (0 when unparseable)
 */
export function parseCurrency(text) {
    if (typeof text === 'number') return text;
    if (!text) return 0;

    // Keep only digits, separators and sign.
    let cleaned = String(text).replace(/[^0-9.,-]/g, '');

    if (cleaned.indexOf(',') !== -1) {
        // Comma is the decimal separator (lv-LV); dots are thousands groups.
        cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    }

    const value = parseFloat(cleaned);
    return isNaN(value) ? 0 : value;
}

/**
 * Format date for display
 * @param {Date|string} date - Date to format
 * @param {string} locale - Locale code (default: lv-LV)
 * @returns {string} Formatted date string
 */
export function formatDate(date, locale = 'lv-LV') {
    const dateObj = date instanceof Date ? date : new Date(date);
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).format(dateObj);
}

/**
 * Get CSRF token from meta tag or cookie
 * @returns {string} CSRF token value
 */
export function getCsrfToken() {
    // Try to get from Django's CSRF token input
    const tokenInput = document.querySelector('[name=csrfmiddlewaretoken]');
    if (tokenInput) {
        return tokenInput.value;
    }
    
    // Fallback to cookie
    const cookie = document.cookie.split(';')
        .find(c => c.trim().startsWith('csrftoken='));
    
    return cookie ? cookie.split('=')[1] : '';
}

/**
 * Make AJAX request with CSRF token
 * @param {string} url - Request URL
 * @param {Object} options - Fetch options
 * @returns {Promise} Fetch promise
 */
export function fetchWithCsrf(url, options = {}) {
    const csrfToken = getCsrfToken();
    
    options.headers = {
        ...options.headers,
        'X-CSRFToken': csrfToken,
        'X-Requested-With': 'XMLHttpRequest'
    };
    
    return fetch(url, options);
}

/**
 * Check if element is in viewport
 * @param {HTMLElement} element - Element to check
 * @returns {boolean} True if element is in viewport
 */
export function isInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

/**
 * Smooth scroll to element
 * @param {string|HTMLElement} target - Target selector or element
 * @param {number} offset - Offset from top (default: 80)
 */
export function scrollToElement(target, offset = 80) {
    const element = typeof target === 'string' 
        ? document.querySelector(target) 
        : target;
    
    if (element) {
        const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({
            top: top,
            behavior: 'smooth'
        });
    }
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (err) {
        // Fallback for older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        
        try {
            document.execCommand('copy');
            document.body.removeChild(textarea);
            return true;
        } catch (err) {
            document.body.removeChild(textarea);
            return false;
        }
    }
}

/**
 * Check if device is mobile using feature detection
 * @returns {boolean} True if device appears to be mobile
 */
export function isMobile() {
    return window.innerWidth <= 768 || 'ontouchstart' in window;
}

/**
 * Parse query string to object
 * @param {string} queryString - Query string to parse
 * @returns {Object} Parsed query parameters
 */
export function parseQueryString(queryString = window.location.search) {
    const params = new URLSearchParams(queryString);
    const result = {};
    
    for (const [key, value] of params) {
        result[key] = value;
    }
    
    return result;
}

/**
 * Get the current language prefix from the URL path.
 * Django i18n_patterns prefixes URLs with /en/, /lv/, etc.
 * @returns {string} Language prefix path segment (e.g., "/en" or "/lv")
 */
/**
 * Translate a string through Django's JavaScriptCatalog (loaded in base.html).
 * Falls back to the source text if the catalog is unavailable.
 */
export function gettext(text) {
    return typeof window.gettext === 'function' ? window.gettext(text) : text;
}

/**
 * Plural-aware translation with %(name)s interpolation, e.g.
 * ngettextFormat('%(count)s character remaining', '%(count)s characters remaining', n, {count: n}).
 */
export function ngettextFormat(singular, plural, count, values) {
    const text = typeof window.ngettext === 'function'
        ? window.ngettext(singular, plural, count)
        : (count === 1 ? singular : plural);
    return typeof window.interpolate === 'function'
        ? window.interpolate(text, values, true)
        : text.replace(/%\((\w+)\)s/g, (_match, key) => values[key]);
}

export function getLangPrefix() {
    const match = window.location.pathname.match(/^\/([a-z]{2})\//);
    return match ? `/${match[1]}` : '';
}

/**
 * Update URL query parameters without page reload
 * @param {Object} params - Parameters to update
 */
export function updateUrlParams(params) {
    const url = new URL(window.location);
    
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, value);
        }
    });
    
    window.history.pushState({}, '', url);
}